import type {TFunction} from 'i18next';
import type {
    RelativeComparison,
    RelativeDateRangePreset,
    RelativeDateRangeSelection
} from '../../../insights/v2/insightsDateRangePresets';
import {nextScheduledRun} from '../utils/nextScheduledRun';
import {readTimeZone, wallTime} from '../utils/timeZone';

/**
 * SURVEY_REPORT config, mirrored by the API (`features/export-engine.md`, Phase 3; `ReportPeriod.kt`):
 * - `surveyId` is required (`surveyName` is the card's snapshot). The rolling periods end yesterday 24:00 in the
 *   organization's zone; LAST_WEEK / LAST_MONTH / LAST_QUARTER are the previous calendar week (Mon–Sun) / month / quarter.
 * - `customDays` 1..366, read only with CUSTOM_DAYS. `formats` absent = PDF. `language` absent = the organization's.
 * - `compare: true` compares with the previous period; `compareMode: 'PREVIOUS_YEAR'` with the same days a year
 *   earlier instead (absent = the previous period, as every config written before it).
 * - `includeInsights` / `includeSensitive` (default off) decide the content for everyone the Team members step picks.
 */

export const REPORT_PERIODS = [
    'YESTERDAY', 'LAST_WEEK', 'LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_MONTH', 'LAST_QUARTER', 'CUSTOM_DAYS'
] as const;

export type ReportPeriod = typeof REPORT_PERIODS[number];

export const DEFAULT_REPORT_PERIOD: ReportPeriod = 'LAST_7_DAYS';

export const REPORT_COMPARE_MODES = ['PREVIOUS_PERIOD', 'PREVIOUS_YEAR'] as const;

export type ReportCompareMode = typeof REPORT_COMPARE_MODES[number];

export const REPORT_FORMATS = ['PDF', 'DOCX', 'XLSX'] as const;

export type ReportFormat = typeof REPORT_FORMATS[number];

export const DEFAULT_REPORT_FORMATS: ReportFormat[] = ['PDF'];

/** Product names, the same in every language. */
export const REPORT_FORMAT_NAMES: Record<ReportFormat, string> = {PDF: 'PDF', DOCX: 'Word', XLSX: 'Excel'};

/** A scheduled report is German or English; the download also has Italian, the automation does not. */
export const REPORT_LANGUAGES = ['de', 'en'] as const;

export const MIN_CUSTOM_DAYS = 1;
export const MAX_CUSTOM_DAYS = 366;

const isOneOf = <T extends string>(values: readonly T[], value: unknown): value is T =>
    typeof value === 'string' && (values as readonly string[]).includes(value);

export const hasReportSurvey = (config?: Record<string, any> | null): boolean =>
    typeof config?.surveyId === 'string' && !!config.surveyId.trim();

/** An absent or unknown period reads as the default the editor writes for a new step. */
export const readReportPeriod = (config?: Record<string, any> | null): ReportPeriod =>
    isOneOf(REPORT_PERIODS, config?.period) ? config!.period : DEFAULT_REPORT_PERIOD;

/** What the report compares with; null without `compare`. An absent or unknown mode is the previous period. */
export const readReportCompareMode = (config?: Record<string, any> | null): ReportCompareMode | null => {
    if (config?.compare !== true) return null;
    return config.compareMode === 'PREVIOUS_YEAR' ? 'PREVIOUS_YEAR' : 'PREVIOUS_PERIOD';
};

/** Whole days in range, else undefined: CUSTOM_DAYS without them cannot run. */
export const readCustomDays = (config?: Record<string, any> | null): number | undefined => {
    const raw = config?.customDays;
    const days = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;
    return Number.isInteger(days) && days >= MIN_CUSTOM_DAYS && days <= MAX_CUSTOM_DAYS ? days : undefined;
};

/** Known formats in the editor's order; no list at all is the API's default. */
export const readReportFormats = (config?: Record<string, any> | null): ReportFormat[] => {
    const stored: unknown[] = Array.isArray(config?.formats) ? config!.formats : DEFAULT_REPORT_FORMATS;
    return REPORT_FORMATS.filter(format => stored.includes(format));
};

/** de or en; undefined when the step names none (then `defaultReportLanguage` applies). */
export const readReportLanguage = (config?: Record<string, any> | null): string | undefined =>
    isOneOf(REPORT_LANGUAGES, config?.language) ? config!.language : undefined;

/** A step without a language reports in English for an English-speaking organization, else in German (as the API does). */
export const defaultReportLanguage = (organizationLanguage?: string | null): string =>
    organizationLanguage?.trim().toLowerCase().split(/[-_]/)[0] === 'en' ? 'en' : 'de';

/** As the period picker names it: "Last month", "Last 14 days". */
export const reportPeriodLabel = (config: Record<string, any> | null | undefined, t: TFunction): string => {
    switch (readReportPeriod(config)) {
        case 'YESTERDAY':
            return t('Yesterday');
        case 'LAST_WEEK':
            return t('Last week');
        case 'LAST_30_DAYS':
            return t('Last 30 days');
        case 'LAST_MONTH':
            return t('Last month');
        case 'LAST_QUARTER':
            return t('Last quarter');
        case 'CUSTOM_DAYS': {
            const days = readCustomDays(config);
            if (!days) return t('Custom number of days');
            return days === 1 ? t('Yesterday') : t('Last {{count}} days', {count: days});
        }
        default:
            return t('Last 7 days');
    }
};

/** The card's period: "Last week, compared with the previous year". */
export const reportPeriodSummary = (config: Record<string, any> | null | undefined, t: TFunction): string => {
    const period = reportPeriodLabel(config, t);
    switch (readReportCompareMode(config)) {
        case 'PREVIOUS_PERIOD':
            return t('{{period}}, compared with the previous period', {period});
        case 'PREVIOUS_YEAR':
            return t('{{period}}, compared with the previous year', {period});
        default:
            return period;
    }
};

const PERIOD_PRESETS: Record<ReportPeriod, RelativeDateRangePreset> = {
    YESTERDAY: 'yesterday',
    LAST_WEEK: 'lastWeek',
    LAST_7_DAYS: 'last7Days',
    LAST_30_DAYS: 'last30Days',
    LAST_MONTH: 'lastMonth',
    LAST_QUARTER: 'lastQuarter',
    CUSTOM_DAYS: 'lastNDays'
};

/**
 * The date control's relative selection for a stored config. A stored `customDays` out of range is passed on as it
 * is, so the control's field shows what the summary complains about.
 */
export const reportPeriodSelection = (config?: Record<string, any> | null): RelativeDateRangeSelection => {
    const period = readReportPeriod(config);
    const compareMode = readReportCompareMode(config);
    const rawDays = config?.customDays;
    return {
        preset: PERIOD_PRESETS[period],
        days: period !== 'CUSTOM_DAYS' ? undefined : typeof rawDays === 'number' ? rawDays : readCustomDays(config),
        comparison: compareMode === null ? null : compareMode === 'PREVIOUS_YEAR' ? 'previousYear' : 'previousPeriod'
    };
};

/**
 * The config keys a relative selection writes. `customDays` belongs to CUSTOM_DAYS and `compareMode` to a comparison
 * with the previous year only, so the previous period keeps the shape every older config has.
 */
export const reportPeriodPatch = (selection: RelativeDateRangeSelection): Record<string, any> => {
    const period = (Object.keys(PERIOD_PRESETS) as ReportPeriod[]).find(key => PERIOD_PRESETS[key] === selection.preset)
        ?? DEFAULT_REPORT_PERIOD;
    const comparison: RelativeComparison | null = selection.comparison;
    return {
        period,
        customDays: period === 'CUSTOM_DAYS' ? selection.days : undefined,
        compare: comparison !== null,
        compareMode: comparison === 'previousYear' ? 'PREVIOUS_YEAR' : undefined
    };
};

/**
 * The organization's calendar day of the step's next run, as a local midnight for the date control: the scheduled
 * trigger's next slot when one is above the step, else today. Periods count back from it, as the API's do.
 */
export const reportRunDay = (
    triggerConfig: Record<string, any> | null | undefined,
    zone: string | undefined,
    now: Date
): Date => {
    // `now` as the last slot: a slot that already fired today is not "due" again; a "once" not yet fired is its start.
    const next = triggerConfig
        ? nextScheduledRun(triggerConfig, now, now) ?? nextScheduledRun(triggerConfig, undefined, now)
        : null;
    const at = next?.at ?? now;
    const usable = readTimeZone(zone);
    if (!usable) return new Date(at.getFullYear(), at.getMonth(), at.getDate());

    const wall = new Date(wallTime(at.getTime(), usable));
    return new Date(wall.getUTCFullYear(), wall.getUTCMonth(), wall.getUTCDate());
};

/** "PDF and Excel", in the UI language's list style. */
export const formatReportFormats = (formats: ReportFormat[], language: string): string =>
    new Intl.ListFormat(language, {type: 'conjunction'}).format(formats.map(format => REPORT_FORMAT_NAMES[format]));
